import Button from '../../../../../../../../common/_custom/Button/Button';
import EditableField from '../../../../../../../../common/_custom/EditableField/EditableField';
import LoadingSpinner from '../../../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Modal from '../../../../../../../../common/_custom/Modal/Modal';
import { numberOnly } from '../../../../../../../../utils/utils';
import ConfirmationPopup from '../../../../../../../../common/ConfirmationPopup/ConfirmationPopup';
import {
    FILL_ONCE_COPY,
    LINKEDIN_CLEAR_CONFIRM_COPY,
    LINKEDIN_REFRESH_COPY,
    PHONE_COUNTRY_PREFIX,
} from '../../../../../../../../common/ManagementProfile/managementProfileFields';
import { useEditDirectorDetailsModal } from './useEditDirectorDetailsModal';
import type { EditDirectorDetailsModalProps } from '../ManagementUserProfile.types';
import styles from './EditDirectorDetailsModal.module.scss';

const PHONE_MAX_LENGTH = 10;
const PAN_MAX_LENGTH = 10;
const DIN_MAX_LENGTH = 8;
const noop = () => {};

function EditDirectorDetailsModal({ open, onClose, director, onSave, linkedinRefreshing = false }: EditDirectorDetailsModalProps) {
    const fieldStyles = {
        groupClassName: styles.FieldGroup,
        labelClassName: styles.FieldLabel,
        labelRowClassName: styles.FieldLabelRow,
        containerClassName: styles.FieldContainer,
        inputClassName: styles.FieldInput,
        readOnlyInputClassName: styles.ReadOnlyInput,
        prefixRowClassName: styles.PhoneRow,
    };

    const {
        values,
        errors,
        saving,
        isSaveDisabled,
        setField,
        handleSave,
        confirmingLinkedInClear,
        confirmLinkedInClear,
        dismissLinkedInClear,
        confirmingFillOnce,
        confirmFillOnce,
        dismissFillOnce,
        isFieldEditable,
    } = useEditDirectorDetailsModal({
        director,
        onSave,
        onClose,
        linkedinRefreshing,
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="flexible"
            persistent
            style={{ padding: 0 }}
            closeIconStyle={{ height: '0.875rem', width: '0.875rem', top: '1.6rem', right: '1.5rem' }}
        >
            <div className={styles.EditDirectorDetailsModal}>
                <div className={styles.Header}>
                    <div className={styles.HeadingGroup}>
                        <h3 className={styles.Heading}>Edit Details</h3>
                        <span className={styles.HeaderDivider} aria-hidden="true" />
                        <span className={styles.DirectorName}>{director?.name}</span>
                    </div>
                </div>

                <div className={styles.Body}>
                    <EditableField {...fieldStyles} label="Name" value={director?.name ?? ''} editable={false} onChange={noop} />

                    <div className={styles.FieldRow}>
                        <EditableField
                            {...fieldStyles}
                            label="PAN"
                            value={values.pan}
                            editable={isFieldEditable('pan')}
                            onChange={(value) => setField('pan', value)}
                            placeholder="Add pan"
                            maxLength={PAN_MAX_LENGTH}
                            errorMessage={errors.pan}
                            footnote={
                                isFieldEditable('pan') && !errors.pan ? (
                                    <span className={styles.FillOnceHint}>{FILL_ONCE_COPY.HINT}</span>
                                ) : undefined
                            }
                        />
                        <EditableField
                            {...fieldStyles}
                            label="DIN"
                            value={values.din}
                            editable={isFieldEditable('din')}
                            onChange={(value) => setField('din', value)}
                            placeholder="Add din"
                            maxLength={DIN_MAX_LENGTH}
                            errorMessage={errors.din}
                            footnote={
                                isFieldEditable('din') && !errors.din ? (
                                    <span className={styles.FillOnceHint}>{FILL_ONCE_COPY.HINT}</span>
                                ) : undefined
                            }
                        />
                    </div>

                    <EditableField
                        {...fieldStyles}
                        label="Email"
                        value={values.email}
                        editable
                        onChange={(value) => setField('email', value)}
                        placeholder="Add email"
                        errorMessage={errors.email}
                    />

                    <EditableField
                        {...fieldStyles}
                        label="Phone No"
                        value={values.phone}
                        editable
                        onChange={(value) => setField('phone', numberOnly(value))}
                        placeholder="Add phone number"
                        maxLength={PHONE_MAX_LENGTH}
                        errorMessage={errors.phone}
                        containerClassName={styles.PhoneFieldContainer}
                        prefix={<span className={styles.CountryPrefix}>{PHONE_COUNTRY_PREFIX}</span>}
                    />

                    <EditableField
                        {...fieldStyles}
                        label="LinkedIn"
                        value={values.linkedinUrl}
                        editable
                        onChange={(value) => setField('linkedinUrl', value)}
                        placeholder="Add LinkedIn profile URL"
                        errorMessage={errors.linkedinUrl}
                        labelSlot={
                            linkedinRefreshing ? (
                                <span className={styles.FetchingLabel}>
                                    <LoadingSpinner height="0.75rem" color="var(--primary-purple)" />
                                    {LINKEDIN_REFRESH_COPY.FETCHING}
                                </span>
                            ) : undefined
                        }
                        footnote={
                            linkedinRefreshing ? <span className={styles.FetchingHint}>{LINKEDIN_REFRESH_COPY.STALE_HINT}</span> : undefined
                        }
                    />

                    <EditableField
                        {...fieldStyles}
                        label="Address"
                        value={values.address}
                        editable
                        onChange={(value) => setField('address', value)}
                        placeholder="Add address"
                        errorMessage={errors.address}
                    />

                    <EditableField
                        {...fieldStyles}
                        label="Overview"
                        value={values.managementProfileSummary}
                        editable
                        onChange={(value) => setField('managementProfileSummary', value)}
                        placeholder="Add overview"
                        type="textarea"
                        textAreaRows={5}
                        errorMessage={errors.managementProfileSummary}
                        inputClassName={styles.OverviewInput}
                    />
                </div>

                {confirmingFillOnce && (
                    <ConfirmationPopup
                        open
                        title={FILL_ONCE_COPY.CONFIRM_TITLE}
                        subTitle={FILL_ONCE_COPY.CONFIRM_SUBTITLE}
                        confirmCta={FILL_ONCE_COPY.CONFIRM}
                        cancelCta={FILL_ONCE_COPY.CANCEL}
                        onConfirm={confirmFillOnce}
                        onCancel={dismissFillOnce}
                    />
                )}

                {confirmingLinkedInClear && (
                    <ConfirmationPopup
                        open
                        title={LINKEDIN_CLEAR_CONFIRM_COPY.TITLE}
                        subTitle={LINKEDIN_CLEAR_CONFIRM_COPY.SUBTITLE}
                        confirmCta={LINKEDIN_CLEAR_CONFIRM_COPY.CONFIRM}
                        cancelCta={LINKEDIN_CLEAR_CONFIRM_COPY.CANCEL}
                        onConfirm={confirmLinkedInClear}
                        onCancel={dismissLinkedInClear}
                    />
                )}

                <div className={styles.Footer}>
                    <Button
                        text="Save"
                        variant="primary"
                        size="wide"
                        onClick={handleSave}
                        loading={saving}
                        disabled={isSaveDisabled}
                        className={styles.SaveButton}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default EditDirectorDetailsModal;
